import { RefObject, useEffect } from 'react';

export function useTriggerOnClickOutsideElement(
  elementRef: RefObject<HTMLElement>,
  onClickOutside: (e: MouseEvent) => void,
  enabled: boolean,
  preventDefault = false,

  // Determines when the click handler is triggered:
  //  - true: The handler is called immediately when the event starts (before the click hits the target element).
  //  - false: The handler is called after the event is processed by the target element (at the ascent stage).
  // In some cases, we use 'true' to capture the click as early as possible
  // (so that there is no case when several dropdowns are open at the same time),
  // but by default we set the value to 'false' (for example, when necessary
  // in order for the target element to execute its logic first)
  isListenEarly = false,
) {
  useEffect(() => {
    if (enabled) {
      const clickListener = (e: MouseEvent) => {
        // if preventDefault is used, it's not possible to focus inputs within popups
        if (preventDefault) e.preventDefault();
        if (!elementRef.current?.contains(e.target as Node)) {
          onClickOutside(e);
        }
      };
      let unmounted = false;
      // without setTimeout the StyledAutocomplete immediately closes after opening
      setTimeout(
        () =>
          !unmounted &&
          window.addEventListener('mousedown', clickListener, isListenEarly),
      );

      return () => {
        unmounted = true;
        window.removeEventListener('mousedown', clickListener, isListenEarly);
      };
    }
    return;
  }, [enabled, onClickOutside]);
}
